<script setup>
  import {computed, ref} from "vue";

  import ComponentCategoryBadge from "@components/Badges/CategoryBadge.vue";
  import ComponentBmpShowDialog from "@components/Dialogs/BmpShowDialog.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {router} from "@inertiajs/core";
  import {MedHoverCard, MedIcon, MedTooltip} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientsPatientInformation",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    target: {
      type: String,
      default: "_self", // _blank
    },
  });
  const refOverallRecordStatusDialog = ref(null);
  const refShareInfoDialog = ref(null);
  const refBmpShowDialog = ref(null);

  const overallRecordStatusDialogTitle = ref("");
  const overallRecordStatusDialogText = ref("");

  function openBmpShowDialog() {
    refBmpShowDialog.value.open();
  }

  function openOverallRecordStatusDialog() {
    switch (props.patient.overall_record_status) {
      case "serviceRecommended": {
        overallRecordStatusDialogTitle.value = "Erneute Leistungserbringung möglich";
        overallRecordStatusDialogText.value = "Letzte abgeschlossene Medikationsanalyse vor über einem Jahr.";
        break;
      }
      case "inProgress": {
        overallRecordStatusDialogTitle.value = "In Bearbeitung";
        overallRecordStatusDialogText.value = "Patientenakte enthält zu bearbeitende Medikationsanalysen.";
        break;
      }
      case "completed": {
        overallRecordStatusDialogTitle.value = "Abgeschlossen";
        overallRecordStatusDialogText.value =
          "Patientenakte enthält nur abgeschlossene oder irrelevante Medikationsanalysen.";
        break;
      }
      case "discarded": {
        overallRecordStatusDialogTitle.value = "Als irrelevant markiert";
        overallRecordStatusDialogText.value = "Patientenakte enthält nur als irrelevant markierte Medikationsanalysen.";
        break;
      }
    }

    refOverallRecordStatusDialog.value.open();
  }

  function openPatient(patient) {
    // allow to open in new window
    if (props.target === "_blank" || props.target === "blank") {
      const url = route("patients.show", {patient: patient.id});
      window.open(url, "_blank");
    } else {
      router.visit(route("patients.show", {patient: patient.id}));
    }
  }

  const overallRecordStatusColor = computed(() => {
    switch (props.patient.overall_record_status) {
      case "serviceRecommended": {
        return "blue";
      }
      case "inProgress": {
        return "orange";
      }
      case "completed": {
        return "green";
      }
      case "discarded": {
        return "gray";
      }
    }

    return "error";
  });
</script>

<template>
  <div class="flex items-center space-x-1">
    <!-- share status (not shown on deleted-patients tab due to missing shared_owners) -->
    <template v-if="patient.is_fake && patient.shared_owners">
      <MedHoverCard>
        <button
          class="cursor-pointer"
          :data-test="getTestId('patient-information-open-ref-share-info-button')"
          @click.prevent="refShareInfoDialog.open()"
        >
          <ComponentCategoryBadge class="px-1! py-0.5!">
            <MedIcon v-if="!patient.revoked" icon="call_received" />
            <MedIcon v-if="patient.revoked" icon="person_add_disabled" />
          </ComponentCategoryBadge>
        </button>

        <template #content>
          <div class="flex items-center space-x-2">
            <MedIcon v-if="!patient.revoked" icon="call_received" />
            <MedIcon v-if="patient.revoked" icon="person_add_disabled" />

            <div v-if="patient.shared_owners.length > 0">
              <div v-if="!patient.revoked" class="font-thin!">
                {{ patient.shared_owners[0]?.created_diff }} für mich freigegeben von
              </div>

              <div v-if="patient.revoked" class="font-thin!">
                {{ patient.shared_owners[0]?.updated_diff }} widerrufen von
              </div>

              <div>
                {{ patient.shared_owners[0]?.company ?? patient.shared_owners[0]?.name }}

                <template v-if="patient.shared_owners[0]?.company && patient.shared_owners[0]?.name">
                  ({{ patient.shared_owners[0]?.name }})
                </template>
              </div>
            </div>
          </div>
        </template>
      </MedHoverCard>
    </template>

    <!-- overall status -->
    <MedTooltip
      v-if="patient.records_count"
      :content="patient.records_count === 1 ? '1 Medikationsanalyse' : patient.records_count + ' Medikationsanalysen'"
    >
      <button
        class="cursor-pointer"
        :data-test="getTestId(`patient-information-${patient.overall_record_status}`)"
        @click.prevent="openOverallRecordStatusDialog()"
      >
        <ComponentCategoryBadge :color="overallRecordStatusColor" class="flex items-center space-x-px px-1! py-0.5!">
          <ComponentIcon class="text-lg">data_saver_off</ComponentIcon>
          <span class="tabular-nums">{{ patient.records_count }}</span>
        </ComponentCategoryBadge>
      </button>
    </MedTooltip>

    <!-- outgoing shared records or sharelinks -->
    <MedHoverCard v-if="patient.shared_by_me_count > 0">
      <template #content>
        <div class="flex items-center space-x-2">
          <MedIcon icon="call_made" />

          <div>
            <span class="font-thin!">enthält</span>
            {{ patient.shared_by_me_count }}
            geteilte
            {{ patient.shared_by_me_count === 1 ? "Analyse" : "Analysen" }}
          </div>
        </div>
      </template>

      <ComponentCategoryBadge class="px-1! py-0.5!">
        <ComponentIcon class="text-lg">call_made</ComponentIcon>
      </ComponentCategoryBadge>
    </MedHoverCard>

    <!-- status -->
    <MedTooltip v-if="patient.in_nursing_home" content="Heimpatient">
      <ComponentCategoryBadge class="px-1! py-0.5!">
        <ComponentIcon class="text-lg">local_hotel</ComponentIcon>
      </ComponentCategoryBadge>
    </MedTooltip>

    <MedTooltip v-if="patient.from_wawi" content="aus der Warenwirtschaft">
      <ComponentCategoryBadge class="px-1! py-0.5!">
        <ComponentIcon class="text-lg">shopping_cart</ComponentIcon>
      </ComponentCategoryBadge>
    </MedTooltip>

    <MedTooltip v-if="patient.mp_date && $page.props.client.license.is_pro" content="gespeicherter BMP">
      <button
        class="cursor-pointer"
        :data-test="getTestId('patient-information-open-bmp-show')"
        @click.prevent="openBmpShowDialog(patient)"
      >
        <ComponentCategoryBadge class="px-1! py-0.5!">
          <ComponentIcon class="text-lg">qr_code_scanner</ComponentIcon>
        </ComponentCategoryBadge>
      </button>
    </MedTooltip>
  </div>

  <ComponentDialog
    ref="refOverallRecordStatusDialog"
    :dialog-title="overallRecordStatusDialogTitle"
    primary-label="Patienten öffnen"
    :primary-button-disabled="patient.deleted_at !== null"
    :dialog-content="overallRecordStatusDialogText"
    :test-id="getTestId('patient-information-record-status')"
    @primary-button-clicked="openPatient(patient)"
  />

  <ComponentDialog
    ref="refShareInfoDialog"
    dialog-type="information"
    dialog-title="Fallfreigabe und Zusammenarbeit"
    dialog-width="medium"
    :test-id="getTestId('patient-information-share')"
  >
    <template #content>
      <div class="flow-root">
        <ul class="space-y-8">
          <li v-if="patient.revoked" class="relative [&:last-child>span]:hidden">
            <span class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />

            <div class="relative flex space-x-3">
              <div>
                <span class="flex size-8 items-center justify-center rounded-full bg-gray-400 ring-8 ring-white">
                  <ComponentIcon class="text-xl text-white">person_add_disabled</ComponentIcon>
                </span>
              </div>

              <div class="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                <div>
                  <p class="text-sm text-gray-700">Freigabe wurde widerrufen von</p>

                  <div>
                    <strong class="font-semibold">
                      {{ patient.shared_owners[0]?.company ?? patient.shared_owners[0]?.name }}
                    </strong>

                    <template v-if="patient.shared_owners[0]?.company && patient.shared_owners[0]?.name">
                      ({{ patient.shared_owners[0]?.name }})
                    </template>
                  </div>
                  <div class="mt-2"><!-- no button --></div>
                </div>

                <div class="text-right text-sm text-gray-500">
                  <MedTooltip content="widerrufen am">
                    <time class="whitespace-nowrap">
                      {{ patient.shared_owners[0].updated_at }}
                    </time>
                  </MedTooltip>
                </div>
              </div>
            </div>
          </li>

          <li class="relative [&:last-child>span]:hidden">
            <span class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />

            <div class="relative flex space-x-3">
              <div>
                <span class="flex size-8 items-center justify-center rounded-full bg-gray-400 ring-8 ring-white">
                  <ComponentIcon class="text-xl text-white">call_received</ComponentIcon>
                </span>
              </div>

              <div class="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                <div>
                  <p class="text-sm text-gray-700">Dieser Patient wurde mit Ihnen geteilt von</p>

                  <div>
                    <strong class="font-semibold">
                      {{ patient.shared_owners[0]?.company ?? patient.shared_owners[0]?.name }}
                    </strong>

                    <template v-if="patient.shared_owners[0]?.company && patient.shared_owners[0]?.name">
                      ({{ patient.shared_owners[0]?.name }})
                    </template>
                  </div>

                  <div class="mt-2"><!-- no button --></div>
                </div>

                <div class="text-right text-sm text-gray-500">
                  <MedTooltip content="freigegeben am">
                    <time class="whitespace-nowrap">
                      {{ patient.shared_owners[0].created_at }}
                    </time>
                  </MedTooltip>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </template>
  </ComponentDialog>

  <ComponentBmpShowDialog ref="refBmpShowDialog" :patient="patient" />
</template>
